/**
 * IT-797 (SEO, п.10 и п.14) — шаблоны мета-тегов и alt для однотипных страниц.
 *
 * Правило из ТЗ: шаблон подставляется только там, где мета-теги НЕ заданы вручную
 * в админке. Ручной ввод всегда в приоритете — для этого есть `preferManual`.
 *
 * Тексты шаблонов согласованы с документами:
 *  - «Шаблон мета-тегов для страниц квартир и помещений» (лист «Шаблоны»);
 *  - п.10 ТЗ — проекты, «Кладовые», «Паркинг».
 */

/** Значение из админки в приоритете, шаблон — как fallback. */
export const preferManual = (manual?: string | null, template?: string | null): string =>
  (typeof manual === "string" && manual.trim() ? manual.trim() : template || "").trim();

/** 14450928 → «14 450 928» (неразрывные пробелы Intl приводим к обычным). */
export const formatRub = (value?: number | string | null): string => {
  const num = typeof value === "string" ? Number(value.replace(/[^\d.]/g, "")) : value;
  if (!num || Number.isNaN(num)) return "";
  return new Intl.NumberFormat("ru-RU").format(Math.round(num)).replace(/ /g, " ");
};

/** Города, которые узнаём в адресе без подсказки «г.», и их форма для оборота «в …». */
const KNOWN_CITIES: Record<string, string> = {
  Екатеринбург: "Екатеринбурге",
  Москва: "Москве",
  "Санкт-Петербург": "Санкт-Петербурге",
  Тюмень: "Тюмени",
  Пермь: "Перми",
  Челябинск: "Челябинске",
  Сочи: "Сочи",
};

/** «Екатеринбург» → «Екатеринбурге» (для оборота «в …»). */
export const cityIn = (city?: string | null): string => {
  const name = (city || "").trim();
  if (!name) return "Екатеринбурге";
  if (KNOWN_CITIES[name]) return KNOWN_CITIES[name];
  // грубое правило: город на согласную → +«е» («Курган» → «Кургане»)
  return /[бвгджзклмнпрстфхцчшщ]$/i.test(name) ? `${name}е` : name;
};

/** Адрес проекта из админки — строка свободного формата, с <br> и лишними пробелами. */
export const cleanAddress = (address?: string | null): string =>
  (address || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/[​ ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[,;]\s*$/, "");

/**
 * «Екатеринбург, ул. Металлургов» → «Екатеринбург». Городом считаем только
 * известный город или слово с пометкой «г.»: адрес вида «Репина-Крауля» начинается
 * с улицы, и раньше она попадала в заголовок как «в Репина» (IT-1253, п. 10).
 */
export const cityFromAddress = (address?: string | null, fallback = "Екатеринбург"): string => {
  const clean = cleanAddress(address);
  if (!clean) return fallback;
  const first = clean.split(/[,—–]/)[0].trim();
  const marked = first.match(/^г\.?\s*([А-ЯЁ][А-Яа-яЁё-]+)$/);
  if (marked) return marked[1];
  return KNOWN_CITIES[first] ? first : fallback;
};

/** Адрес для description: если он начинается не с города, город подставляем сами. */
export const addressWithCity = (address?: string | null, fallback = "Екатеринбург"): string => {
  const clean = cleanAddress(address);
  if (!clean) return "";
  const first = clean.split(/[,—–]/)[0].trim();
  const startsWithCity = /^г\.?\s/.test(first) || Boolean(KNOWN_CITIES[first]);
  return startsWithCity ? clean : `${fallback}, ${clean}`;
};

/** Из «Дом 3» получаем «3»; если формат другой — отдаём как есть. */
export const houseNumber = (house?: string | null): string => {
  const value = (house || "").trim();
  if (!value) return "";
  const match = value.match(/(?:дом|корпус|к\.)\s*([\dA-Za-zА-Яа-я\-./]+)/i);
  return match ? match[1] : value;
};

/**
 * Из строки-факта в шапке страницы («парковочные места от 1 925 000 р.»,
 * «от 15 056 ₽/мес») достаём готовую формулировку цены: «от 1 925 000 ₽»,
 * «от 15 056 ₽/мес». Именно эти строки SEO-специалист использовал в примерах.
 */
export const priceClause = (factDescription?: string | null): string => {
  const text = (factDescription || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  const match = text.match(/от\s+([\d\s]+?)\s*(₽|руб\.?|р\.)?\s*(\/\s*мес\.?)?(?=$|[,;.]|\s[а-яa-z])/i);
  if (!match) return "";
  const digits = (match[1] || "").replace(/\s+/g, " ").trim();
  if (!digits) return "";
  const perMonth = match[3] ? "/мес" : "";
  return `от ${digits} ₽${perMonth}`;
};

/* ------------------------------------------------------------------ */
/* Квартиры                                                            */
/* ------------------------------------------------------------------ */

export interface FlatSeoInput {
  /** details.type — «1-комнатная», «Студия» */
  type?: string | null;
  area?: number | string | null;
  flatnum?: string | null;
  price?: number | null;
  projectName?: string | null;
  city?: string | null;
  floor?: string | number | null;
  /** project.house — «Дом 3» */
  house?: string | null;
  moveinDate?: string | null;
}

const isStudio = (type?: string | null) => /студи/i.test(type || "");

/**
 * «2-комнатная квартира» / «Студия».
 *
 * Отступление от буквы ТЗ: в документе шаблон записан как «[количество комнат] квартира»,
 * что даёт неграмотное «2 квартира». Состав данных сохранён, добавлено «-комнатная».
 */
export const flatRoomsLabel = (type?: string | null): string => {
  const value = (type || "").trim();
  if (!value) return "Квартира";
  if (isStudio(value)) return "Студия";
  return /комнатн/i.test(value) ? `${value} квартира` : `${value} квартира`;
};

const areaLabel = (area?: number | string | null) => (area || area === 0 ? `${area} м²` : "");

/** H1: «2-комнатная квартира 65.2 м² № 1.16.11» */
export const flatSeoH1 = (flat: FlatSeoInput): string =>
  [flatRoomsLabel(flat.type), areaLabel(flat.area), flat.flatnum ? `№ ${flat.flatnum}` : ""].filter(Boolean).join(" ");

/**
 * Title: «2-комнатная квартира 65.2 м² №1.16.11: купить по цене 14 450 928 ₽
 * в ЖК Атлас Ауро Екатеринбург, этаж 16, дом 1»
 */
export const flatSeoTitle = (flat: FlatSeoInput): string => {
  const head = [flatRoomsLabel(flat.type), areaLabel(flat.area), flat.flatnum ? `№${flat.flatnum}` : ""]
    .filter(Boolean)
    .join(" ");
  const price = formatRub(flat.price);
  const where = [flat.projectName ? `в ЖК ${flat.projectName}` : "", (flat.city || "").trim()]
    .filter(Boolean)
    .join(" ");
  const tail = [
    price ? `купить по цене ${price} ₽` : "купить от застройщика",
    where,
    flat.floor ? `этаж ${flat.floor}` : "",
    houseNumber(flat.house) ? `дом ${houseNumber(flat.house)}` : "",
  ].filter(Boolean);
  const [buy, place, ...rest] = tail;
  const start = [buy, place].filter(Boolean).join(" ");
  return `${head}: ${[start, ...rest].filter(Boolean).join(", ")}`;
};

/**
 * Description: «В продаже 2-комнатная квартира №1.16.11 площадью 65.2 м² стоимостью
 * 14 450 928 ₽ в ЖК Атлас Ауро от застройщика АТЛАС ДЕВЕЛОПМЕНТ ✓ Дом: 1 ✓ Этаж: 16
 * ✓ Срок сдачи: 2 кв. 2026 ✓ Планировка на сайте»
 */
export const flatSeoDescription = (flat: FlatSeoInput): string => {
  const price = formatRub(flat.price);
  const head = [
    "В продаже",
    flatRoomsLabel(flat.type),
    flat.flatnum ? `№${flat.flatnum}` : "",
    areaLabel(flat.area) ? `площадью ${areaLabel(flat.area)}` : "",
    price ? `стоимостью ${price} ₽` : "",
    flat.projectName ? `в ЖК ${flat.projectName}` : "",
    "от застройщика АТЛАС ДЕВЕЛОПМЕНТ",
  ]
    .filter(Boolean)
    .join(" ");
  const facts = [
    houseNumber(flat.house) ? `✓ Дом: ${houseNumber(flat.house)}` : "",
    flat.floor ? `✓ Этаж: ${flat.floor}` : "",
    flat.moveinDate ? `✓ Срок сдачи: ${flat.moveinDate}` : "",
    "✓ Планировка на сайте",
  ].filter(Boolean);
  return `${head} ${facts.join(" ")}`;
};

/**
 * alt изображения квартиры (п.14 ТЗ).
 * Первое изображение — без номера, второе и далее — с суффиксом «-2», «-3»…
 * («2-комнатная квартира 45 м² № 101-2»).
 */
export const flatImageAlt = (flat: FlatSeoInput, index = 0): string => {
  const suffix = index > 0 ? `-${index + 1}` : "";
  const num = flat.flatnum ? `№ ${flat.flatnum}${suffix}` : "";
  return [flatRoomsLabel(flat.type), areaLabel(flat.area), num].filter(Boolean).join(" ");
};

/**
 * Добавляет к готовому alt порядковый номер изображения:
 * «… № 101» → «… № 101-2». Первое изображение остаётся без номера.
 */
export const withImageIndex = (alt: string, index = 0): string => (index > 0 ? `${alt}-${index + 1}` : alt);

/* ------------------------------------------------------------------ */
/* Коммерческие помещения                                              */
/* ------------------------------------------------------------------ */

export interface CommerceSeoInput {
  /** Название помещения из Аллио — «Помещение 2-223 34.6 м²» */
  title?: string | null;
  /** Вид недвижимости — «Офисное помещение», «Помещение свободного назначения» */
  kind?: string | null;
  area?: number | string | null;
  /** Номер помещения — «2-223» */
  number?: string | null;
  price?: number | null;
  projectName?: string | null;
  city?: string | null;
  house?: string | null;
  moveinDate?: string | null;
}

const commerceKind = (kind?: string | null) => (kind || "").trim() || "Помещение";

/** Пометки менеджеров в назначении помещения, которые в заголовок не годятся. */
const COMMERCE_SERVICE_KINDS = new Set(["моп", "неликвид"]);

/**
 * Вид помещения из назначения в каталоге (IT-1253, п. 10). `appointments` приходит
 * из Аллио строкой через запятую — «Офисное помещение, МОП», — берём первое
 * содержательное значение. Пусто — шаблон подставит нейтральное «Помещение».
 */
export const commerceKindFromAppointments = (appointments?: string | null): string => {
  if (typeof appointments !== "string") return "";
  return (
    appointments
      .split(",")
      .map((part) => part.trim())
      .find((part) => part && !COMMERCE_SERVICE_KINDS.has(part.toLowerCase())) || ""
  );
};

/** Из «Помещение 2-223 34.6 м²» вытаскиваем «2-223». */
export const commerceNumber = (input: CommerceSeoInput): string => {
  if (input.number) return String(input.number).trim();
  // сначала убираем площадь («34.6 м²»), иначе она сама похожа на номер
  const withoutArea = (input.title || "").replace(/[\d]+(?:[.,][\d]+)?\s*м(?:2|²|кв)?\b/gi, " ");
  const tokens = withoutArea
    .replace(/№/g, " ")
    .split(/\s+/)
    .filter((token) => /\d/.test(token));
  return tokens.length ? tokens[0].replace(/[.,;]+$/, "") : "";
};

/** H1: «Офисное помещение 34.6 м² №2-223» */
export const commerceSeoH1 = (item: CommerceSeoInput): string => {
  const num = commerceNumber(item);
  return [commerceKind(item.kind), areaLabel(item.area), num ? `№${num}` : ""].filter(Boolean).join(" ");
};

/** Title: «Офис 34.6 м² №2-223: купить по цене 10 034 000 ₽ в ЖК 4Ю Екатеринбург» */
export const commerceSeoTitle = (item: CommerceSeoInput): string => {
  const num = commerceNumber(item);
  const head = [commerceKind(item.kind), areaLabel(item.area), num ? `№${num}` : ""].filter(Boolean).join(" ");
  const price = formatRub(item.price);
  const where = [item.projectName ? `в ЖК ${item.projectName}` : "", (item.city || "").trim()]
    .filter(Boolean)
    .join(" ");
  const tail = [price ? `купить по цене ${price} ₽` : "купить от застройщика", where].filter(Boolean).join(" ");
  return `${head}: ${tail}`;
};

/**
 * Description: «В продаже офисное помещение №2-223 площадью 34.6 м² стоимостью
 * 10 034 000 ₽ в ЖК 4Ю от застройщика АТЛАС ДЕВЕЛОПМЕНТ ✓ Дом: 1-4 ✓ Срок сдачи: СДАН
 * ✓ Планировка на сайте»
 */
export const commerceSeoDescription = (item: CommerceSeoInput): string => {
  const num = commerceNumber(item);
  const price = formatRub(item.price);
  const kind = commerceKind(item.kind);
  const head = [
    "В продаже",
    kind.charAt(0).toLowerCase() + kind.slice(1),
    num ? `№${num}` : "",
    areaLabel(item.area) ? `площадью ${areaLabel(item.area)}` : "",
    price ? `стоимостью ${price} ₽` : "",
    item.projectName ? `в ЖК ${item.projectName}` : "",
    "от застройщика АТЛАС ДЕВЕЛОПМЕНТ",
  ]
    .filter(Boolean)
    .join(" ");
  const facts = [
    item.house ? `✓ Дом: ${houseNumber(item.house)}` : "",
    item.moveinDate ? `✓ Срок сдачи: ${item.moveinDate}` : "",
    "✓ Планировка на сайте",
  ].filter(Boolean);
  return `${head} ${facts.join(" ")}`;
};

/* ------------------------------------------------------------------ */
/* Проекты / Кладовые / Паркинг                                        */
/* ------------------------------------------------------------------ */

export interface PlaceSeoInput {
  projectName?: string | null;
  city?: string | null;
  /** Адрес проекта из админки — «Екатеринбург, ул. Московская, 249» */
  address?: string | null;
  /** Ближайшее метро, если известно */
  metro?: string | null;
  /** Готовая формулировка цены — «от 15 056 ₽/мес», «от 1 925 000 ₽» */
  priceFrom?: string | null;
}

/**
 * Проект. Title: «ЖК ПАРК СТОЛИЦ в Екатеринбурге: купить квартиру в новостройке
 * от застройщика по выгодной цене»
 */
export const projectSeoTitle = (input: PlaceSeoInput): string => {
  if (!input.projectName) return "";
  return `ЖК ${input.projectName} в ${cityIn(input.city)}: купить квартиру в новостройке от застройщика по выгодной цене`;
};

/**
 * Проект. Description: «Купить квартиру в новостройке в ЖК ПАРК СТОЛИЦ от официального
 * застройщика Atlas Development! ✔ Екатеринбург, ул. Московская, 249, рядом с метро
 * Чкаловская ✔ Смотрите актуальные цены, планировки и ход строительства на сайте!»
 */
export const projectSeoDescription = (input: PlaceSeoInput): string => {
  if (!input.projectName) return "";
  const place = [cleanAddress(input.address), input.metro ? `рядом с метро ${input.metro}` : ""]
    .filter(Boolean)
    .join(", ");
  const head = `Купить квартиру в новостройке в ЖК ${input.projectName} от официального застройщика Atlas Development!`;
  const tail = "✔ Смотрите актуальные цены, планировки и ход строительства на сайте!";
  return [head, place ? `✔ ${place}` : "", tail].filter(Boolean).join(" ");
};

/**
 * Кладовые. Title: «Кладовые в ЖК АТЛАС ВИЗИОН от 15 056 ₽/мес в Екатеринбурге:
 * купить у застройщика Atlas Development»
 *
 * Отступление от буквы ТЗ: в документе шаблон говорит «аренда», но кладовые
 * продаются, а не сдаются. Сумма в шапке страницы — ежемесячный платёж, и слово
 * «аренда» рядом с ней вводило бы в заблуждение и посетителя, и поиск.
 */
export const storageSeoTitle = (input: PlaceSeoInput): string => {
  if (!input.projectName) return "";
  const price = (input.priceFrom || "").trim();
  return [
    `Кладовые в ЖК ${input.projectName}`,
    price,
    `в ${cityIn(input.city)}: купить у застройщика Atlas Development`,
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Кладовые. Description: «Кладовые в новостройке в ЖК АТЛАС ВИЗИОН ✔ Екатеринбург,
 * ул. Металлургов ✔ Платёж от 15 056 ₽/мес ✔ Подробная информация на сайте!»
 */
export const storageSeoDescription = (input: PlaceSeoInput): string => {
  if (!input.projectName) return "";
  const price = (input.priceFrom || "").trim();
  return [
    `Кладовые в новостройке в ЖК ${input.projectName}`,
    cleanAddress(input.address) ? `✔ ${cleanAddress(input.address)}` : "",
    price ? `✔ Платёж ${price}` : "",
    "✔ Подробная информация на сайте!",
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Паркинг. Title: «Машиноместа в ЖК 4YOU от 1 925 000 ₽ в Екатеринбурге:
 * купить по выгодной цене от застройщика»
 */
export const parkingSeoTitle = (input: PlaceSeoInput): string => {
  if (!input.projectName) return "";
  const price = (input.priceFrom || "").trim();
  return [
    `Машиноместа в ЖК ${input.projectName}`,
    price,
    `в ${cityIn(input.city)}: купить по выгодной цене от застройщика`,
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Паркинг. Description: «Купить парковочное место в паркинге в ЖК 4YOU
 * ✔ Екатеринбург, ул. Онежская — пер. Столярный, стоимостью от 1 925 000 ₽
 * ✔ Подробная информация на сайте!»
 */
export const parkingSeoDescription = (input: PlaceSeoInput): string => {
  if (!input.projectName) return "";
  const price = (input.priceFrom || "").trim();
  const place = [addressWithCity(input.address), price ? `стоимостью ${price}` : ""].filter(Boolean).join(", ");
  return [
    `Купить парковочное место в паркинге в ЖК ${input.projectName}`,
    place ? `✔ ${place}` : "",
    "✔ Подробная информация на сайте!",
  ]
    .filter(Boolean)
    .join(" ");
};

/** Паркинг. H1: «Машиноместа в ЖК 4YOU» */
export const parkingSeoH1 = (input: PlaceSeoInput): string =>
  input.projectName ? `Машиноместа в ЖК ${input.projectName}` : "";
